import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { buildApiUrl } from '../lib/api';
import {
  asContentArray,
  asContentObject,
  fetchContentBlocks,
  getContentValue,
} from '../lib/content';
import {
  buildPostPath,
  formatPostDate,
  getReadTimeLabel,
  normalizePosts,
} from '../lib/posts';

async function fetchLatestPosts() {
  const params = new URLSearchParams({ page: '1', limit: '3' }).toString();
  const response = await fetch(buildApiUrl(`/blog?${params}`));

  if (!response.ok) {
    throw new Error('Failed to fetch latest posts');
  }

  const payload = await response.json();

  return normalizePosts(Array.isArray(payload) ? payload : payload.data || []);
}

function resolveHref(value, fallback) {
  return typeof value === 'string' && value.trim() ? value : fallback;
}

function ActionLink({ href, className, children }) {
  const resolvedHref = resolveHref(href, '/');

  if (/^https?:\/\//i.test(resolvedHref)) {
    return (
      <a href={resolvedHref} className={className} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  }

  return (
    <Link to={resolvedHref} className={className}>
      {children}
    </Link>
  );
}

export default function Main() {
  const { data: contentBlocks = [] } = useQuery({
    queryKey: ['siteContent'],
    queryFn: fetchContentBlocks,
  });

  const { data: latestPosts = [] } = useQuery({
    queryKey: ['homeLatestPosts'],
    queryFn: fetchLatestPosts,
  });

  const homeValue = getContentValue(contentBlocks, ['home', 'homepage'], {});
  const homeContent = asContentObject(homeValue, {});
  const focusCards = asContentArray(homeContent.sections);
  const summaryMetrics = asContentArray(homeContent.stats);
  const heroEyebrow = homeContent.eyebrow || '';
  const heroTitle = homeContent.title || '';
  const heroSubtitle = homeContent.subtitle || '';
  const primaryCtaLabel = homeContent.primaryCtaLabel || '';
  const primaryCtaHref = resolveHref(homeContent.primaryCtaHref, '/contact');
  const secondaryCtaLabel = homeContent.secondaryCtaLabel || '';
  const secondaryCtaHref = resolveHref(homeContent.secondaryCtaHref, '/blog');
  const closingTitle = homeContent.closingTitle || 'Journal entries';
  const closingBody = homeContent.closingBody || '';
  const hasHeroContent =
    Boolean(heroTitle.trim()) ||
    Boolean(heroSubtitle.trim()) ||
    Boolean(primaryCtaLabel.trim()) ||
    Boolean(secondaryCtaLabel.trim()) ||
    focusCards.length > 0 ||
    summaryMetrics.length > 0;

  return (
    <main className="page-shell">
      <div className="page-width">
        {hasHeroContent && (
          <section className="page-header">
            {heroEyebrow && <p className="eyebrow">{heroEyebrow}</p>}
            {heroTitle && <h1 className="section-title mt-6">{heroTitle}</h1>}
            {heroSubtitle && <p className="section-subtitle max-w-2xl">{heroSubtitle}</p>}
            {(primaryCtaLabel || secondaryCtaLabel) && (
              <div className="mt-10 flex flex-wrap justify-center gap-4">
                {primaryCtaLabel && (
                  <ActionLink href={primaryCtaHref} className="primary-button">
                    {primaryCtaLabel}
                  </ActionLink>
                )}
                {secondaryCtaLabel && (
                  <ActionLink href={secondaryCtaHref} className="secondary-button">
                    {secondaryCtaLabel}
                  </ActionLink>
                )}
              </div>
            )}
          </section>
        )}

        {focusCards.length > 0 && (
          <section className={`${hasHeroContent ? 'mt-20' : ''} grid gap-6 md:grid-cols-3`}>
            {focusCards.map((card, index) => {
              const cardContent = asContentObject(card, {});

              return (
                <article key={cardContent.title || index} className="surface-card p-7 md:p-8">
                  <p className="eyebrow">{cardContent.eyebrow || 'Section'}</p>
                  <h2 className="mt-5 text-2xl font-medium leading-tight text-slate-900">
                    {cardContent.title || 'Untitled section'}
                  </h2>
                  {(cardContent.body || cardContent.description) && (
                    <p className="mt-4 text-sm leading-8 text-slate-600">
                      {cardContent.body || cardContent.description}
                    </p>
                  )}
                </article>
              );
            })}
          </section>
        )}

        {summaryMetrics.length > 0 && (
          <section className="mt-8">
            <div className="surface-card p-8 md:p-10">
              <p className="eyebrow">Summary</p>
              <div className="mt-6 grid gap-4 md:grid-cols-3">
                {summaryMetrics.map((metric, index) => {
                  const metricContent = asContentObject(metric, {});

                  return (
                    <div key={metricContent.label || index} className="rounded-[22px] border border-slate-100 bg-slate-50/70 px-5 py-5">
                      <p className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
                        {metricContent.label || 'Metric'}
                      </p>
                      <p className="mt-4 font-display text-4xl leading-none text-slate-900">
                        {metricContent.value || '-'}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        <section className="mt-8">
          <div className="surface-card p-8 md:p-10">
            <div className="max-w-2xl">
              <p className="eyebrow">Journal</p>
              <h2 className="mt-5 font-display text-5xl leading-none tracking-tight text-slate-900">
                {closingTitle}
              </h2>
              {closingBody && <p className="mt-5 text-sm leading-8 text-slate-600">{closingBody}</p>}
            </div>

            {latestPosts.length > 0 ? (
              <div className="mt-10 grid gap-6 lg:grid-cols-3">
                {latestPosts.map((post) => (
                  <article key={post.id} className="rounded-[24px] border border-slate-100 bg-slate-50/70 p-6">
                    <div className="flex h-full flex-col">
                      <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
                        {formatPostDate(post.createdAt)}
                      </p>
                      <h3 className="mt-4 text-2xl font-medium leading-tight text-slate-900">
                        {post.title || 'Untitled post'}
                      </h3>
                      <p className="mt-4 flex-1 text-sm leading-8 text-slate-600">
                        {post.excerpt || 'No excerpt has been added yet.'}
                      </p>
                      <div className="mt-6 flex items-center justify-between gap-4 text-[11px] uppercase tracking-[0.22em] text-slate-500">
                        <span>{getReadTimeLabel(post.content)}</span>
                        <Link to={buildPostPath(post)} className="text-link">
                          Read post
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="mt-10 rounded-[24px] border border-slate-100 bg-slate-50/70 p-8 text-sm leading-8 text-slate-600">
                No blog posts have been published yet.
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
